import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { UsuariosListaDao } from "../../domain/models/usuariosLista";
import { initialUsuariosListaFiltersValues, useUsuariosListaFilters } from "../hooks/usuarios_lista/filters";
import { useUsuariosListaPagination } from "../hooks/usuarios_lista/pagination";
import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { NroUsuariosTotal } from "../../domain/models/nro_usuarios_total";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

type LoadUsuariosListaState = {
  idsCentroCusto?: number[],
  idsGrupoAcesso?: number[],
  nome?: string,
  login?: string,
  email?: string,
  unidade?: string,
  status?: number,

  page?: number;
  rowsPerPage?: number;
}

export const useUsuariosListaViewModel = () => {

  const {
    administracao: {
      getAllUsuarioUseCase,
      deleteUsuarioUseCase,
      bloqueioUsuarioUseCase,
      getNumeroUsuariosTotalUseCase,
    },
    commons: {
      getCentroCustoDropDownUseCase,
      getGrupoAcessoDropDownUseCase,
    },

  } = useDependencyInjection();

  const navigate = useNavigate();
  const backOrNavigate = useBackOrNavigate();

  const { pushErrorNotification, pushInfoNotification, setPageLoading } = usePageLoader();
  const debouncedRef = useCreateDebounceHandler(1500);

  const { idUsuario } = useUserAuthStore();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedIdUsuario, setSelectedIdUsuario] = useState<number>();
  const [openBloqueioModal, setOpenBloqueioModal] = useState(false);
  const [internalErrorMessage, setInternalErrorMessage] = useState<string | undefined>();
  const [nroDeUsuariosTotal, setNroDeUsuariosTotal] = useState<NroUsuariosTotal>();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [usuariosLista, setUsuariosLista] = useState<UsuariosListaDao[]>();
  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
  const [ddlGrupoAcesso, setDdlGrupoAcesso] = useState<DropDown[]>();

  const filters = useUsuariosListaFilters();
  const pagination = useUsuariosListaPagination();

  //#region CARREGAMENTO.

  const loadNumeroUsuariosTotal = useCallback(async () => {
    try {
      const result = await getNumeroUsuariosTotalUseCase.execute();
      setNroDeUsuariosTotal(result);

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar número total de usuários!'));
    }
  }, [getNumeroUsuariosTotalUseCase,
    pushErrorNotification
  ]);


  const loadUsuariosLista = useCallback(async (funcState?: LoadUsuariosListaState) => {

    try {
      setLoading(true);
      const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);
      const filteredNome = evaluateParamValue(funcState?.nome, filters.nome);
      const filteredLogin = evaluateParamValue(funcState?.login, filters.login);
      const filteredEmail = evaluateParamValue(funcState?.email, filters.email);
      const filteredIdsGrupoAcesso = evaluateParamValue(funcState?.idsGrupoAcesso, filters.idsGrupoAcesso);
      const filteredStatus = evaluateParamValue(funcState?.status, filters.status);

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllUsuarioUseCase.execute(
        filteredNome ?? '',
        filteredLogin ?? '',
        filteredEmail ?? '',
        filteredStatus ?? 2,
        filteredIdsGrupoAcesso ?? [],
        filteredIdsCentroCusto ?? [],

        filteredPage,
        filteredRowsPerPage,
      );

      setUsuariosLista(getAllUsuarioUseCase.items);
      await loadNumeroUsuariosTotal();
      pagination.setRowsPerPage(getAllUsuarioUseCase.pageSize);
      pagination.setTotalItems(getAllUsuarioUseCase.totalItems);

    } catch (e: unknown) {
      setUsuariosLista([]);
      pushErrorNotification("Error", processError(e, "Falha ao buscar lista de usuarios!"));
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    pagination,
    getAllUsuarioUseCase,
    pushErrorNotification,
    loadNumeroUsuariosTotal
  ]);


  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      setPageLoading(true);
      const response = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(response);
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar Centro de Custo!'));
    } finally {
      setPageLoading(false);
    }
  }, [getCentroCustoDropDownUseCase,
    pushErrorNotification]);


  const loadGruposAcessoDropDown = useCallback(async () => {
    try {
      setPageLoading(true);
      const response = await getGrupoAcessoDropDownUseCase.getGrupoAcessoDropDown();
      setDdlGrupoAcesso(response);
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar Grupo de Acesso!'));
    } finally {
      setPageLoading(false);
    }
  }, [getGrupoAcessoDropDownUseCase,
    pushErrorNotification]);


  //#endregion


  //#region PAGE START BOOTSTRAP. 

  usePageStartBootstrap(async () => {

    try {
      await loadUsuariosLista();
      await loadGruposAcessoDropDown();
      await loadCentroCustoDropDown();

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar lista de usuarios!")[0]);
    }
  });

  //#endregion






  //#region NOVO/EDITAR.

  const handleNewUsuarios = useCallback(() => {
    navigate("/Usuarios_cadastro");
  }, [navigate]);

  const handleEditUsuarios = useCallback(async (idUsuario: number | null, openNewTab: boolean) => {
    if (idUsuario) {
      if (openNewTab) {
        window.open(`/Usuarios_cadastro?id=${idUsuario}`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/Usuarios_cadastro?id=${idUsuario}`, { alternativeRoute: '/Usuarios_lista' });
      }
    } else {
      if (openNewTab) {
        window.open(`/Usuarios_cadastro`, '_blank', 'noopener,noreferrer');
      } else {
        backOrNavigate(`/Usuarios_cadastro`, { alternativeRoute: '/Usuarios_lista' });
      }
    }
  }, [
    backOrNavigate,
  ]);

  //#endregion



  //#region PAGINAÇÃO.


  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadUsuariosLista();

    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de usuarios!'));
    } finally {
      setLoading(false);
    }
  }, [loadUsuariosLista]);

  const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
    setRowSelectionModel(rows);
  }, []);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);

      await loadUsuariosLista({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de usuarios!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadUsuariosLista]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);
      await loadUsuariosLista({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de usuarios!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadUsuariosLista]);

  //#endregion

  //#region Bloqueio/Desbloqueio

  const handleUsuariosBloqueioOpenState = useCallback((idUsuario: number | null, openState: boolean) => {
    if (openState) {
      if (idUsuario) {
        setSelectedIdUsuario(idUsuario);
        setOpenBloqueioModal(true);
      } else {
        setSelectedIdUsuario(undefined);
        setOpenBloqueioModal(false);
      }
    }
    setOpenBloqueioModal(openState);
  }, []);


  const handleConfirmarBloqueioUsuarios = useCallback(async () => {
    try {

      setSaving(true);
      if (selectedIdUsuario) {
        await bloqueioUsuarioUseCase.execute(selectedIdUsuario);
        pushInfoNotification("Sucesso", " Usuario bloqueado com sucesso!", { isAutoFinish: true });
      } else {
        pushErrorNotification("Erro", "Não foi possivel bloquear Usuario!", { isAutoFinish: true });

      }

      await loadCentroCustoDropDown()
      await loadUsuariosLista()
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao Bloquear Usuario!"));
    } finally {
      setSaving(false);
      setOpenBloqueioModal(false);
      setSelectedIdUsuario(undefined)
    }
  }, [
    selectedIdUsuario,
    bloqueioUsuarioUseCase,
    loadCentroCustoDropDown,
    loadUsuariosLista,
    pushErrorNotification,
    pushInfoNotification]);


  //#endregion



  //#region DELECAO

  const handleUsuariosDeleteOpenState = useCallback((idUsuario: number | null, openState: boolean) => {
    if (openState) {
      if (idUsuario) {
        setSelectedIdUsuario(idUsuario);
        setOpenDeleteModal(true);
      } else {
        setSelectedIdUsuario(undefined);
        setOpenDeleteModal(false);
      }
    }
    setOpenDeleteModal(openState);
  }, []);


  const handleConfirmarDeletarUsuarios = useCallback(async () => {
    try {

      setSaving(true);
      if (selectedIdUsuario) {
        await deleteUsuarioUseCase.execute(selectedIdUsuario);
        pushInfoNotification("Sucesso", " Usuario deletado com sucesso!", { isAutoFinish: true });
      } else {
        pushErrorNotification("Erro", "Não foi possivel deletar Usuario!", { isAutoFinish: true });

      }

      await loadCentroCustoDropDown()
      await loadUsuariosLista()
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao Deletar Usuario!"));
    } finally {
      setSaving(false);
      setOpenDeleteModal(false);
      setSelectedIdUsuario(undefined)
    }
  }, [
    selectedIdUsuario,
    deleteUsuarioUseCase,
    loadCentroCustoDropDown,
    loadUsuariosLista,
    pushErrorNotification,
    pushInfoNotification]);


  //#endregion





  //#region PESQUISA.

  const handleIdsCentroCustoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: number[]) => {
      filters.setIdsCentroCusto(newValues)
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ idsCentroCusto: newValues, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    }, [filters,
    pagination,
    loadUsuariosLista]
  );

  const handleIdsGrupoAcessoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: number[]) => {
      filters.setIdsGrupoAcesso(newValues)
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ idsGrupoAcesso: newValues, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    }, [filters,
    pagination,
    loadUsuariosLista]
  );

  const handleNomeChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {

      filters.setNome(newValue ?? undefined);
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ nome: newValue ?? undefined, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadUsuariosLista, pushErrorNotification]
  );

  const handleLoginChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {

      filters.setLogin(newValue ?? undefined);
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ login: newValue ?? undefined, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadUsuariosLista, pushErrorNotification]
  );

  const handleEmailChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {

      filters.setEmail(newValue ?? undefined);
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ email: newValue ?? undefined, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadUsuariosLista, pushErrorNotification]
  );

  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (newValue: number | null) => {

      filters.setStatus(newValue ?? undefined);
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadUsuariosLista({ status: newValue ?? undefined, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar usuarios!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [filters, pagination, loadUsuariosLista, pushErrorNotification]
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadUsuariosLista({
        ...initialUsuariosListaFiltersValues,
        page: 1
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar usuarios!'));
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    pagination,
    loadUsuariosLista,
    pushErrorNotification]);

  //#endregion 

  return {
    idUsuario,
    loading,
    saving,
    usuariosLista: usuariosLista ?? [],
    errorMessage,
    filters,
    pagination,
    openDeleteModal,
    ddlCentroCusto: ddlCentroCusto ?? [],
    ddlGrupoAcesso: ddlGrupoAcesso ?? [],
    openBloqueioModal,
    internalErrorMessage,
    selectedIdUsuario,
    rowSelectionModel,
    nroDeUsuariosTotal,

    handleRowSelectionModelChange,
    setInternalErrorMessage,
    handleUsuariosBloqueioOpenState,
    handleConfirmarBloqueioUsuarios,
    handleNewUsuarios,
    handleEditUsuarios,
    handleUsuariosDeleteOpenState,
    handleConfirmarDeletarUsuarios,
    handleIdsGrupoAcessoChange,
    handleNomeChange,
    handleLoginChange,
    handleEmailChange,
    handleStatusChange,
    handleIdsCentroCustoChange,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleClearFilters,


  };
}


